import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SidePanel } from '@/admin-area/ui/SidePanel';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api-client';
import { MANUAL_STATUSES, type ManualStatus, StatusBadge } from '../list/expense-labels';
import type { Expense } from './use-expense';

export type StatusChangeOutcome =
  | { kind: 'changed'; expense: Expense }
  /** Someone changed the expense since it was read (409): the card reloads it. */
  | { kind: 'conflict' };

function NoteField({
  noteId,
  note,
  tried,
  onNote,
}: {
  readonly noteId: string;
  readonly note: string;
  readonly tried: boolean;
  readonly onNote: (note: string) => void;
}) {
  const { t } = useTranslation('expenses');
  const noteMissing = note.trim() === '';
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={noteId} className="text-sm font-semibold">
        {t('detail.statusChange.note')}{' '}
        <span className="text-muted-foreground font-normal">
          {t('detail.statusChange.required')}
        </span>
      </label>
      <textarea
        id={noteId}
        value={note}
        required
        aria-invalid={tried && noteMissing}
        onChange={(event) => onNote(event.target.value)}
        rows={3}
        className="border-input bg-card rounded-md border px-3 py-2 text-sm"
      />
      {tried && noteMissing ? (
        <span role="alert" className="text-destructive text-sm">
          {t('detail.statusChange.noteRequired')}
        </span>
      ) : (
        <span className="text-muted-foreground text-sm">{t('detail.statusChange.hint')}</span>
      )}
    </div>
  );
}

function StatusForm({
  formId,
  noteId,
  expense,
  choices,
  status,
  note,
  tried,
  failed,
  onStatus,
  onNote,
  onSubmit,
}: {
  readonly formId: string;
  readonly noteId: string;
  readonly expense: Expense;
  readonly choices: readonly ManualStatus[];
  readonly status: ManualStatus | null;
  readonly note: string;
  readonly tried: boolean;
  readonly failed: boolean;
  readonly onStatus: (status: ManualStatus) => void;
  readonly onNote: (note: string) => void;
  readonly onSubmit: () => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <form
      id={formId}
      noValidate
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <p className="text-muted-foreground flex items-center gap-2 text-sm">
        {t('detail.statusChange.now')} <StatusBadge status={expense.status} />
      </p>
      <fieldset className="flex flex-col gap-1.5">
        <legend className="mb-1 text-sm font-semibold">{t('detail.statusChange.newStatus')}</legend>
        {choices.map((choice) => (
          <label key={choice} className="flex min-h-9 items-center gap-2">
            <input
              type="radio"
              name={`${formId}-status`}
              value={choice}
              checked={status === choice}
              onChange={() => onStatus(choice)}
            />
            <span>{t(`list.status.${choice}`)}</span>
          </label>
        ))}
        {tried && status === null ? (
          <span role="alert" className="text-destructive text-sm">
            {t('detail.statusChange.statusRequired')}
          </span>
        ) : null}
      </fieldset>
      <NoteField noteId={noteId} note={note} tried={tried} onNote={onNote} />
      {failed ? (
        <p role="alert" className="text-destructive text-sm">
          {t('detail.statusChange.failed')}
        </p>
      ) : null}
    </form>
  );
}

/** Sends the change; `null` when the request failed (the caller says so). */
async function postStatus(
  expense: Expense,
  status: ManualStatus,
  note: string,
): Promise<StatusChangeOutcome | null> {
  try {
    const { data, response } = await apiClient.POST('/v1/expenses/{expenseId}/status', {
      params: { path: { expenseId: expense.id }, header: { 'If-Match': expense.version } },
      body: { status, note },
    });
    if (response.status === 409) return { kind: 'conflict' };
    if (!response.ok || data === undefined) return null;
    return { kind: 'changed', expense: data };
  } catch {
    return null;
  }
}

/**
 * «Cambia stato» (the board's side panel; design §2.2, manual states): administrators holding
 * `manage` set any state of the lifecycle by hand until release 2's workflow, always with a
 * reason, kept in the history. Sent with `If-Match: <version>` so a change made meanwhile is not
 * overwritten.
 */
export function StatusChangePanel({
  open,
  onOpenChange,
  expense,
  title,
  onDone,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly expense: Expense;
  /** «Trenitalia · 79,90 €», as the card names the expense. */
  readonly title: string;
  readonly onDone: (outcome: StatusChangeOutcome) => void;
}) {
  const { t } = useTranslation('expenses');
  const formId = useId();
  const noteId = useId();
  // Decision 177: an expense in no report is presented only through a report, so by hand it can
  // only wait for its document (the server answers 422 REPORT_REQUIRED otherwise).
  const inNoReport = ((expense as { reportId?: string | null }).reportId ?? null) === null;
  const choices = MANUAL_STATUSES.filter(
    (status) => status !== expense.status && (!inNoReport || status === 'awaiting_document'),
  );
  const [status, setStatus] = useState<ManualStatus | null>(null);
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (open) {
      setStatus(null);
      setNote('');
      setTried(false);
      setFailed(false);
    }
  }, [open]);

  const noteMissing = note.trim() === '';

  async function submit() {
    setTried(true);
    if (status === null || noteMissing || sending) return;
    setSending(true);
    setFailed(false);
    const outcome = await postStatus(expense, status, note.trim());
    if (outcome === null) setFailed(true);
    else onDone(outcome);
    setSending(false);
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title={t('detail.statusChange.title')}
      description={t('detail.statusChange.description', { expense: title })}
      footer={
        <>
          <Button type="submit" form={formId} disabled={sending}>
            {t('detail.statusChange.submit')}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('detail.cancel')}
          </Button>
        </>
      }
    >
      <StatusForm
        formId={formId}
        noteId={noteId}
        expense={expense}
        choices={choices}
        status={status}
        note={note}
        tried={tried}
        failed={failed}
        onStatus={setStatus}
        onNote={setNote}
        onSubmit={() => void submit()}
      />
    </SidePanel>
  );
}
